import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useOutletContext, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Clock,
  Timer,
  StickyNote,
  Stethoscope,
  ClipboardList,
  ClipboardCheck,
  ScanEye,
  HeartPulse,
  ShieldCheck,
  Loader2,
  User,
  CalendarClock,
  Hash,
  FileText,
  FileStack,
  Folder,
  Info,
  Pencil,
  Save,
  RotateCcw,
} from 'lucide-react'
import { apiGet, redirectToLogin } from '../../lib/api.js'
import { nameInitial } from '../../lib/text.js'
import {
  asList,
  formatTimeRange,
  computeDurationMin,
  getDoctorImage,
  chipStyles,
  buildChartIndex,
  hasOralExam,
} from '../../lib/chart-helpers.js'
import { fetchTreatmentFromList } from '../../lib/billing.js'
import { useMe } from '../../lib/me.js'
import { usePatientLive } from '../../lib/patientLive.js'
import { canEditSection, clinicalEditState, canCreateChart, isAdmin } from '../../lib/role.js'
import { ChartSaveContext, useChartSave } from '../../lib/chartSave.jsx'
import { apptStatusInfo } from '../../data/appointmentStatuses.js'
import ClinicalEditor from '../../components/ClinicalEditor.jsx'
import VitalSignsEditor from '../../components/VitalSignsEditor.jsx'
import URIScreeningEditor, { URIScreeningDialog, isURIScreened } from '../../components/URIScreeningEditor.jsx'
import OralExamEditor from '../../components/OralExamEditor.jsx'
import DocumentEditor from '../../components/DocumentEditor.jsx'
import DocumentGenerator from '../../components/DocumentGenerator.jsx'
import ConsentEditor from '../../components/ConsentEditor.jsx'
import ChartVisitNav from '../../components/patient/ChartVisitNav.jsx'
import { Badge, Banner, Button, ErrorState, Loading, Textarea, fmtDateText, fmtDateTime, useToast } from '../../kit'

const SECTION_LIST = [
  { id: 'appointment', label: 'Appointment', icon: CalendarClock },
  { id: 'treatment',   label: 'Treatment',   icon: Stethoscope },
  { id: 'clinical',    label: 'Clinical',    icon: ClipboardList },
  { id: 'vital-signs', label: 'Vital Signs', icon: HeartPulse },
  { id: 'uri-screening', label: 'URI Screening', icon: ShieldCheck },
  { id: 'oral-exam',   label: 'Full Mouth Exam',   icon: ScanEye },
  { id: 'documents',   label: 'Documents',   icon: FileStack },
  { id: 'consent',     label: 'Consent',     icon: FileText },
  { id: 'file',        label: 'File',        icon: Folder },
]

/**
 * Sticky in-page tab nav. Scroll-spy highlights the section currently in view;
 * clicking a tab smooth-scrolls to it. The pill row scrolls horizontally on
 * mobile so all tabs stay reachable on a narrow viewport.
 */
function SectionTabNav({ sections }) {
  const [activeId, setActiveId] = useState(sections[0]?.id || '')
  const scrollingRef = useRef(false)
  const timerRef = useRef(null)

  useEffect(() => {
    const ids = sections.map((s) => s.id)
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean)
    if (els.length === 0) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (scrollingRef.current) return
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length === 0) return
        // Pick the visible section nearest the top of the viewport.
        const top = visible.reduce((a, b) =>
          a.boundingClientRect.top < b.boundingClientRect.top ? a : b
        )
        setActiveId(top.target.id)
      },
      { rootMargin: '-15% 0px -75% 0px', threshold: 0 }
    )
    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [sections])

  function jump(id) {
    const el = document.getElementById(id)
    if (!el) return
    scrollingRef.current = true
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => { scrollingRef.current = false }, 700)
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setActiveId(id)
  }

  return (
    <div className="sticky top-[calc(3.5rem+var(--ph,5.5rem))] lg:top-[var(--ph,5.5rem)] z-10 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 py-2 mt-4 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b border-slate-200">
      <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
        {sections.map(({ id, label, icon: Icon }) => {
          const active = activeId === id
          return (
            <button
              key={id}
              type="button"
              onClick={() => jump(id)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-full text-[14px] font-medium transition-all ${
                active
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {Icon && <Icon size={12} strokeWidth={2} />}
              {label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// The chart fields this page's editors hold drafts of. A live chart event whose
// fresh copy differs here raises the "โหลดใหม่" pill; one that only touched
// consent[] / document[] / ai_summary (a portal signature, a filed PDF, a
// translation) updates silently. Never add ai_summary here — translating must
// not tell a doctor mid-note that their chart is stale.
const CLINICAL_KEYS = ['history', 'hx', 'oral_exam', 'orthoFirst', 'ortho_JSON', 'uri_screening', 'doctor']
function clinicalBody(c) {
  return JSON.stringify(CLINICAL_KEYS.map((k) => c?.[k] ?? null))
}

export default function PatientChartHistoryDetail() {
  const { id, treatmentId } = useParams()
  const location = useLocation()
  const outletCtx = useOutletContext() || {}
  const patient = outletCtx.patient
  const live = outletCtx.live
  const hn = patient?.hn || patient?.HN || ''
  const passedTreatment = location.state?.treatment || null
  const passedChart     = location.state?.chart     || null
  const me = useMe()
  const toast = useToast()
  const [treatment, setTreatment]     = useState(passedTreatment)
  const [appointment, setAppointment] = useState(passedTreatment?.appointment || null)
  const [chart, setChart]             = useState(passedChart)
  const [error, setError]             = useState(null)
  // Someone else changed this chart while it is open — see the live block below.
  const [chartStale, setChartStale]   = useState(false)
  // False while this visit's chart is still being looked up. `chart === null`
  // then means "not loaded yet", not "this visit has no chart", so สร้าง chart
  // stays hidden — otherwise a click during the ‹ › visit switch could create a
  // duplicate chart for a visit that already has one.
  const [chartResolved, setChartResolved] = useState(!!passedChart)

  // Initial-load effect — runs on mount and when the route changes.
  // After-save refetches use the dedicated handlers below so we don't blow
  // away unrelated state (e.g. saving the treatment shouldn't clear the chart).
  useEffect(() => {
    let alive = true
    setError(null)
    setChartStale(false)

    async function resolveAppointment(t) {
      if (t?.appointment && typeof t.appointment === 'object') {
        if (alive) setAppointment(t.appointment)
      } else if (t?.appointment_id) {
        try {
          const ap = await apiGet(`/api/appointments/${t.appointment_id}`)
          if (alive) setAppointment(ap)
        } catch { /* non-fatal */ }
      }
    }

    async function resolveChart(t) {
      const tid = t?.id ?? t?.treatment_id ?? treatmentId
      if (t?.chart && typeof t.chart === 'object'
          && String(t.chart.treatment ?? t.chart.treatment_id ?? '') === String(tid)) {
        if (alive) setChart(t.chart)
        return
      }
      try {
        // Strict match: only charts whose `treatment` field equals this treatment id.
        const list = await apiGet('/api/charts', {
          patient_id:   id,
          treatment_id: tid,
        })
        const charts = asList(list).filter(
          (c) => String(c?.treatment ?? c?.treatment_id ?? '') === String(tid)
        )
        const idx   = buildChartIndex(charts)
        const match = idx.findFor(t) || null
        if (alive) setChart(match)
      } catch { /* non-fatal */ }
    }

    async function init() {
      // Render passed state immediately for fast first paint, but ALWAYS refetch
      // afterwards so updates made in another tab/browser appear on refresh.
      // Always REPLACE appointment + chart, never keep the previous ones: the
      // ‹ › visit nav changes treatmentId on this same mounted page, and a leftover
      // chart would put visit A's notes (and A's chart id, for any save) under
      // visit B's header until the fetch below lands.
      if (passedTreatment) {
        setTreatment(passedTreatment)
        setAppointment(
          passedTreatment.appointment && typeof passedTreatment.appointment === 'object'
            ? passedTreatment.appointment
            : null
        )
        setChart(passedChart || null)
        setChartResolved(!!passedChart)
      } else {
        setChartResolved(false)
        setTreatment(null); setAppointment(null); setChart(null)
      }

      try {
        const t = await fetchTreatmentFromList(id, treatmentId)
        if (!alive) return
        if (!t) {
          // Only surface "not found" when we don't already have something to show.
          if (!passedTreatment) setError('ไม่พบ treatment ของผู้ป่วยรายนี้')
          return
        }
        setTreatment(t)
        await resolveAppointment(t)
        await resolveChart(t)
      } catch (e) {
        if (alive && !passedTreatment) setError(e?.message || 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (alive) setChartResolved(true)
      }
    }

    init()
    return () => { alive = false }
  }, [id, treatmentId])

  // Per-section edit permissions — see lib/role.js for the full rule set.
  const canEditAppointment = canEditSection('appointment', me, treatment, chart)
  const canEditTreatment   = canEditSection('treatment',   me, treatment, chart)
  const canEditDocuments   = canEditSection('documents',   me, treatment, chart)
  const canEditConsent     = canEditSection('consent',     me, treatment, chart)
  const canEditFile        = canEditSection('file',        me, treatment, chart)

  // Clinical record (Clinical + Full Mouth Exam): role grant + within-the-day
  // time lock. `reason` lets the cards explain why they're locked.
  const clinicalState  = clinicalEditState(me, treatment, chart)
  const canEditClinic  = clinicalState.editable
  const clinicalLockNote = clinicalState.reason === 'time'
    ? 'แก้ไขข้อมูลคลินิกได้เฉพาะภายในวันที่รักษาเท่านั้น (เกินวันรักษาแล้ว)'
    : null

  // Creating a chart for a chartless treatment counts as clinical editing —
  // gate it by the clinical permission too (so a non-owning doctor or a
  // past-day case can't create one), on top of the doctor/admin role rule.
  const canCreate = canCreateChart(me) && canEditClinic

  // Treating doctor's clinical templates (managed in the doctor portal,
  // stored on doctor.template[]). The slim doctor object embedded on a
  // treatment omits them, so fetch the full record once per doctor.
  const treatingDoctorId = treatment?.doctor_id ?? treatment?.doctor?.id ?? null
  const [doctorTemplates, setDoctorTemplates] = useState([])
  useEffect(() => {
    if (!treatingDoctorId) { setDoctorTemplates([]); return }
    let alive = true
    apiGet(`/api/doctors/${treatingDoctorId}`)
      .then((d) => { if (alive) setDoctorTemplates(Array.isArray(d?.template) ? d.template : []) })
      .catch(() => { if (alive) setDoctorTemplates([]) })
    return () => { alive = false }
  }, [treatingDoctorId])

  const [creating, setCreating]   = useState(false)
  const [createErr, setCreateErr] = useState(null)

  // URI screening — optional, intake-style. The create/edit popup is lifted here
  // so both the Documents-section trigger and the URI card's "แก้ไข" reuse it.
  // The card + its nav tab appear only once a screening exists.
  const [uriOpen, setUriOpen] = useState(false)
  const screened = isURIScreened(chart)
  const sections = useMemo(
    () => SECTION_LIST.filter((s) => s.id !== 'uri-screening' || screened),
    [screened],
  )

  // Targeted refetches — each section refreshes only its own data on save,
  // so editing the treatment card doesn't clear the clinical/chart state.
  const refreshTreatment = async () => {
    try {
      const t = await fetchTreatmentFromList(id, treatmentId)
      if (t) setTreatment(t)
    } catch { /* non-fatal */ }
  }
  const refreshAppointment = async () => {
    const apId = appointment?.id ?? appointment?.appointment_id ?? treatment?.appointment_id
    if (!apId) return
    try {
      const ap = await apiGet(`/api/appointments/${apId}`)
      setAppointment(ap)
    } catch { /* non-fatal */ }
  }
  // Live (2026-08-18). The appointment and treatment cards refetch themselves —
  // they are read-only summaries with modal editors, so replacing them mid-read
  // costs nothing.
  //
  // The CHART deliberately does NOT auto-refetch. This page holds the Hx, vital
  // signs, oral exam and document editors: a doctor is often typing a clinical
  // note into one of them, and swapping the `chart` prop underneath would reset
  // the editor and lose what they had written. Losing a clinical note is the
  // worst failure this app can have, so a chart event only raises the pill above
  // the case header and the doctor reloads when they are ready.
  //
  // EXCEPT consent[] and document[] (2026-10-17). Those are written from OUTSIDE
  // this page — the patient signs in the Patient Portal, the in-clinic form files
  // its PDF — and the people watching this page want to see it land. Neither card
  // holds a typed draft, so they are updated live through `liveDocs`, a separate
  // overlay handed ONLY to ConsentEditor + DocumentEditor. `chart` itself is left
  // alone, so the Hx / vital-signs / oral-exam editors (which reset their draft on
  // a new `chart` object) are never touched. The pill still appears when anything
  // ELSE on the chart changed.
  usePatientLive(live, ['appointment'], () => { refreshAppointment() })
  usePatientLive(live, ['treatment'], () => { refreshTreatment() })
  usePatientLive(live, ['chart'], async (ev) => {
    if (!chart || String(ev?.id) !== String(chart.id)) { setChartStale(true); return }
    const fresh = await loadChart()
    if (!fresh) { setChartStale(true); return }
    setLiveDocs({ id: fresh.id, consent: fresh.consent, document: fresh.document, ai_summary: fresh.ai_summary })
    if (clinicalBody(fresh) !== clinicalBody(chart)) setChartStale(true)
  })

  // Keyed by chart id, so an overlay can never leak onto another treatment's chart.
  const [liveDocs, setLiveDocs] = useState(null)
  const docsChart = useMemo(
    () => (chart && liveDocs && String(liveDocs.id) === String(chart.id)
      ? { ...chart, consent: liveDocs.consent, document: liveDocs.document, ai_summary: liveDocs.ai_summary }
      : chart),
    [chart, liveDocs],
  )

  const loadChart = async () => {
    if (!treatment) return undefined
    const tid = treatment.id ?? treatment.treatment_id ?? treatmentId
    try {
      const list = await apiGet('/api/charts', {
        patient_id:   id,
        treatment_id: tid,
      })
      const charts = asList(list).filter(
        (c) => String(c?.treatment ?? c?.treatment_id ?? '') === String(tid)
      )
      const idx = buildChartIndex(charts)
      return idx.findFor(treatment) || null
    } catch { return undefined /* non-fatal */ }
  }
  const refreshChart = async () => {
    const c = await loadChart()
    if (c === undefined) return
    setChart(c)
    setLiveDocs(null)
  }

  // ── Unified save bar: every editable section (Appointment / Treatment /
  // Clinical / Full Mouth Exam) registers its dirty state + save()/cancel() here,
  // so one floating bar can save (or discard) all of them at once. ──
  const [dirtyMap, setDirtyMap] = useState({})
  const [savingAll, setSavingAll] = useState(false)
  const sectionRefs = useRef({})
  const chartSaveCtx = useMemo(() => ({
    report: (sid, isDirty, ref) => {
      if (ref) sectionRefs.current[sid] = ref
      else delete sectionRefs.current[sid]
      setDirtyMap((m) => (m[sid] === isDirty ? m : { ...m, [sid]: isDirty }))
    },
  }), [])
  const dirtyIds = Object.keys(dirtyMap).filter((k) => dirtyMap[k])
  // Each section's save() returns false when it failed (it shows its own inline
  // error + toast); one "บันทึกแล้ว" toast confirms the batch only if none did.
  const saveAll = async () => {
    setSavingAll(true)
    let ok = true
    try {
      for (const sid of dirtyIds) {
        if (await sectionRefs.current[sid]?.current?.save?.() === false) ok = false
      }
    } finally { setSavingAll(false) }
    if (ok) toast.success('บันทึกแล้ว')
  }
  const cancelAll = () => { for (const sid of dirtyIds) sectionRefs.current[sid]?.current?.cancel?.() }

  // Create a chart row for a treatment that has none, then refetch so the
  // clinical/oral-exam editors light up. Inputs come from the treatment;
  // the server stamps `creator` with the logged-in user.
  const createChart = async () => {
    if (!treatment || creating) return
    const tid       = treatment.id ?? treatment.treatment_id ?? treatmentId
    const patientId = treatment.patient_id ?? treatment.patient?.id ?? id
    const doctorId  = treatment.doctor_id  ?? treatment.doctor?.id  ?? null
    setCreating(true); setCreateErr(null)
    try {
      const r = await fetch('/api/charts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ date: treatment.date ?? null, patient: patientId, treatment: tid, doctor: doctorId }),
      })
      if (r.status === 401) { redirectToLogin(); return }
      if (!r.ok) {
        const t = await r.text().catch(() => '')
        throw new Error(`HTTP ${r.status}: ${t || r.statusText}`)
      }
      await refreshChart()
    } catch (e) {
      setCreateErr(e?.message || 'สร้าง chart ไม่สำเร็จ')
    } finally {
      setCreating(false)
    }
  }

  const loading = treatment === null && !error
  return (
    <ChartSaveContext.Provider value={chartSaveCtx}>
    <div>
      <div className="flex items-start justify-between gap-3 mb-4">
        <Link
          to={`/patient/${id}/chart/history`}
          className="inline-flex items-center gap-1.5 text-[14px] text-slate-500 hover:text-brand-700 transition-colors mt-1.5"
        >
          <ArrowLeft size={13} strokeWidth={2} />
          <span>กลับไปยัง History</span>
        </Link>
        <ChartVisitNav patientId={id} treatmentId={treatmentId} blocked={dirtyIds.length > 0} />
      </div>

      {loading && (
        <div className="bg-white border border-slate-200 rounded-2xl">
          <Loading />
        </div>
      )}

      {!loading && error && <ErrorState message={error} />}

      {!loading && !error && treatment && (
        <>
          {chartStale && (
            <Banner
              tone="warn"
              className="mb-3"
              action={<Button size="sm" variant="secondary" onClick={() => { setChartStale(false); refreshChart() }}>โหลดใหม่</Button>}
            >
              มีการแก้ไข chart นี้จากเครื่องอื่น
            </Banner>
          )}
          <CaseHeader treatment={treatment} treatmentId={treatmentId} chart={chart} />
          <SectionTabNav sections={sections} />
          <div className="grid gap-4 mt-4">
            {/* Row 1: appointment + treatment (2-col on md+, stacked below) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div id="appointment" className="scroll-mt-[calc(3.5rem+var(--ph,5.5rem)+3.25rem)] lg:scroll-mt-[calc(var(--ph,5.5rem)+3.25rem)]">
                <AppointmentSection appointment={appointment} treatment={treatment} canEdit={canEditAppointment} onSaved={refreshAppointment} />
              </div>
              <div id="treatment" className="scroll-mt-[calc(3.5rem+var(--ph,5.5rem)+3.25rem)] lg:scroll-mt-[calc(var(--ph,5.5rem)+3.25rem)]">
                <TreatmentSection treatment={treatment} canEdit={canEditTreatment} onSaved={refreshTreatment} />
              </div>
            </div>
            {/* Clinical — full width (inherently wide) */}
            <div id="clinical" className="scroll-mt-[calc(3.5rem+var(--ph,5.5rem)+3.25rem)] lg:scroll-mt-[calc(var(--ph,5.5rem)+3.25rem)]">
              <ClinicalEditor
                chart={chart}
                treatment={treatment}
                canEdit={canEditClinic}
                lockNote={clinicalLockNote}
                canCreate={canCreate && chartResolved}
                creating={creating}
                createError={createErr}
                templates={doctorTemplates}
                onCreate={createChart}
                onSaved={refreshChart}
                aiSummary={docsChart?.ai_summary}
                patientId={id}
                treatmentId={treatment.id ?? treatment.treatment_id ?? treatmentId}
              />
            </div>
            {/* Vital Signs — part of the clinical record */}
            <div id="vital-signs" className="scroll-mt-[calc(3.5rem+var(--ph,5.5rem)+3.25rem)] lg:scroll-mt-[calc(var(--ph,5.5rem)+3.25rem)]">
              <VitalSignsEditor chart={chart} canEdit={canEditClinic} lockNote={clinicalLockNote} me={me} onSaved={refreshChart} />
            </div>
            {/* URI Screening — optional; the card appears only once screened.
                Created/edited via the popup triggered from the Documents section. */}
            {screened && (
              <div id="uri-screening" className="scroll-mt-[calc(3.5rem+var(--ph,5.5rem)+3.25rem)] lg:scroll-mt-[calc(var(--ph,5.5rem)+3.25rem)]">
                <URIScreeningEditor chart={chart} canEdit={canEditDocuments} onEdit={() => setUriOpen(true)} />
              </div>
            )}
            {/* Oral Exam — full width (tooth charts) */}
            <div id="oral-exam" className="scroll-mt-[calc(3.5rem+var(--ph,5.5rem)+3.25rem)] lg:scroll-mt-[calc(var(--ph,5.5rem)+3.25rem)]">
              <OralExamEditor chart={chart} canEdit={canEditClinic} lockNote={clinicalLockNote} onSaved={refreshChart} />
            </div>
            {/* Documents — generate treatment-tied PDFs (before Consent) */}
            <div id="documents" className="scroll-mt-[calc(3.5rem+var(--ph,5.5rem)+3.25rem)] lg:scroll-mt-[calc(var(--ph,5.5rem)+3.25rem)]">
              <DocumentGenerator treatment={treatment} chart={chart} patient={patient} hn={hn} canEdit={canEditDocuments} onSaved={refreshChart} uriScreened={screened} onScreenURI={() => setUriOpen(true)} />
            </div>
            {/* Row 2: consent + file (2-col on lg+, stacked below) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div id="consent" className="scroll-mt-[calc(3.5rem+var(--ph,5.5rem)+3.25rem)] lg:scroll-mt-[calc(var(--ph,5.5rem)+3.25rem)]">
                <ConsentEditor chart={docsChart} patientId={id} canEdit={canEditConsent} isAdmin={isAdmin(me)} onSaved={refreshChart} />
              </div>
              <div id="file" className="scroll-mt-[calc(3.5rem+var(--ph,5.5rem)+3.25rem)] lg:scroll-mt-[calc(var(--ph,5.5rem)+3.25rem)]">
                <DocumentEditor chart={docsChart} hn={hn} canEdit={canEditFile} onSaved={refreshChart} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* URI screening popup — shared by the Documents trigger + the card's edit */}
      {uriOpen && chart && (
        <URIScreeningDialog
          chart={chart}
          me={me}
          onClose={() => setUriOpen(false)}
          onSaved={() => { setUriOpen(false); refreshChart() }}
        />
      )}

      {/* Unified floating save bar — saves all dirty sections at once */}
      {dirtyIds.length > 0 && (
        <div className="fixed inset-x-0 bottom-5 z-30 px-4 pointer-events-none">
          <div className="mx-auto max-w-xl flex items-center gap-3 rounded-2xl bg-slate-900/95 backdrop-blur text-white shadow-2xl shadow-slate-900/30 ring-1 ring-white/10 px-4 py-3 pointer-events-auto animate-ss-pop-in">
            <span className="w-2 h-2 rounded-full bg-amber-400 flex-none animate-pulse" />
            <span className="flex-1 text-[14px] font-medium">
              มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก
              {dirtyIds.length > 1 && <span className="text-slate-400 font-normal"> · {dirtyIds.length} ส่วน</span>}
            </span>
            <button type="button" onClick={cancelAll} disabled={savingAll}
              className="px-3 py-1.5 rounded-lg text-[14px] font-medium text-slate-300 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50">
              ยกเลิกทั้งหมด
            </button>
            <button type="button" onClick={saveAll} disabled={savingAll}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[14px] font-semibold bg-white text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-60">
              {savingAll ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} strokeWidth={2.25} />}
              {savingAll ? 'กำลังบันทึก…' : 'บันทึกทั้งหมด'}
            </button>
          </div>
        </div>
      )}
    </div>
    </ChartSaveContext.Provider>
  )
}


/* -------------------------------------------------------------------------- */
/* Case header                                                                */
/* -------------------------------------------------------------------------- */

function CaseHeader({ treatment, treatmentId, chart }) {
  const al      = treatment.appointmentlist || treatment.appointmentList || {}
  const alType  = al.type || treatment.type || '—'
  const alColor = al.color
  const doctor  = treatment.doctor || {}
  // doctor.code IS the display name in this Xano workspace (e.g. "เอก ธนิตย์")
  const docName = (doctor.code || doctor.name || doctor.full_name || '').trim()
  const docImg  = getDoctorImage(treatment)
  const hasChart = !!chart

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-5 flex-wrap">
      <div className="flex items-center gap-4 min-w-0 flex-1">
        <div className="w-14 h-14 rounded-2xl bg-brand-50 text-brand-700 flex items-center justify-center flex-none ring-1 ring-brand-100">
          <Stethoscope size={22} strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[13.5px] font-semibold"
              style={chipStyles(alColor)}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: chipStyles(alColor).color }} />
              {alType}
            </span>
            <span className="inline-flex items-center gap-1 text-[12px] font-mono text-slate-400">
              <Hash size={10} strokeWidth={2} />{treatmentId}
            </span>
          </div>
          <div className="text-[18px] font-semibold text-slate-900 tracking-tight">
            {fmtDateText(treatment.date, { weekday: true })}
          </div>
          {docName && (
            <div className="text-[14px] text-slate-500 mt-1 flex items-center gap-2">
              <DoctorAvatar src={docImg} name={docName} size={20} />
              <span className="text-slate-700 font-medium">{docName}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        <ChipStatus active={hasChart} icon={<ClipboardCheck size={12} strokeWidth={2.25} />} label="Chart" tone="emerald" />
        <ChipStatus active={hasOralExam(chart)} icon={<ScanEye size={12} strokeWidth={2.25} />} label="Oral" tone="sky" />
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Sections                                                                   */
/* -------------------------------------------------------------------------- */

function AppointmentSection({ appointment, treatment, canEdit, onSaved }) {
  const toast = useToast()
  // Some Xano workspaces put time on the treatment, others on the appointment.
  const a = appointment || {}
  const startTime = a.start_date || a.start_time || treatment.start_date
  const endTime   = a.end_date   || a.end_time   || treatment.end_date
  const duration  = computeDurationMin(startTime, endTime, a.duration ?? treatment.duration)
  const al        = a.appointmentlist || a.appointmentList || treatment.appointmentlist || treatment.appointmentList || {}
  const alType    = al.type || ''
  const alDetail  = al.detail || ''
  const alColor   = al.color
  const timeStr   = formatTimeRange(startTime, endTime)
  const apRemark  = (a.remark || '').trim()
  const status    = a.status || ''

  const initialDetail = a.details ?? a.detail ?? a.note ?? ''
  const apId = a.id ?? a.appointment_id ?? treatment.appointment_id

  const [mode, setMode]       = useState('view')
  const [draft, setDraft]     = useState(initialDetail)
  const [saving, setSaving]   = useState(false)
  const [saveErr, setSaveErr] = useState(null)
  useEffect(() => { setDraft(initialDetail) }, [initialDetail])

  const dirty   = (draft || '') !== (initialDetail || '')
  const canSave = !!apId && dirty && !saving

  // Returns true once saved (false on error) — see saveAll.
  async function save() {
    if (!apId) { setSaveErr('ไม่พบ appointment id'); toast.error('บันทึกไม่สำเร็จ', { detail: 'ไม่พบ appointment id' }); return false }
    setSaving(true); setSaveErr(null)
    try {
      const r = await fetch(`/api/appointments/${apId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ details: draft || '' }),
      })
      if (r.status === 401) { redirectToLogin(); return false }
      if (!r.ok) {
        const t = await r.text().catch(() => '')
        throw new Error(`HTTP ${r.status}: ${t || r.statusText}`)
      }
      setMode('view')
      onSaved?.()
      return true
    } catch (e) {
      const msg = e?.message || 'บันทึกไม่สำเร็จ'
      setSaveErr(msg)
      toast.error('บันทึกไม่สำเร็จ', { detail: msg })
      return false
    } finally {
      setSaving(false)
    }
  }
  function cancel() { setDraft(initialDetail); setMode('view'); setSaveErr(null) }
  function reset()  { setDraft(initialDetail); setSaveErr(null) }
  useChartSave('appointment', dirty, save, cancel)
  async function saveAndConfirm() {
    if (await save()) toast.success('บันทึกแล้ว')
  }

  const right = mode === 'view'
    ? (canEdit && apId ? (
        <Button variant="soft" size="sm" icon={Pencil} onClick={() => { setMode('edit'); setSaveErr(null) }}>แก้ไข</Button>
      ) : null)
    : <EditActions dirty={dirty} saving={saving} canSave={canSave} onReset={reset} onCancel={cancel} onSave={saveAndConfirm} />

  return (
    <Section icon={<CalendarClock size={14} strokeWidth={2} />} title="Appointment" subtitle="ข้อมูลนัดหมาย" right={right}>
      {saveErr && <ErrorState title={saveErr} />}

      <div className="text-[16px] font-semibold text-slate-800 leading-snug">
        {mode === 'view'
          ? (initialDetail || <Muted className="font-normal">ยังไม่ได้ระบุรายละเอียด</Muted>)
          : (
            <Textarea
              value={draft || ''}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="รายละเอียดนัดหมาย"
              rows={2}
              aria-label="รายละเอียดนัดหมาย"
            />
          )}
      </div>

      {(alType || status) && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {alType ? (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[14px] font-semibold"
              style={chipStyles(alColor)}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
              {alType}
              {alDetail && <span className="opacity-80 font-normal">· {alDetail}</span>}
            </span>
          ) : <span />}
          {status && <StatusPill code={status} />}
        </div>
      )}

      {(timeStr || duration != null) && (
        <div className="flex items-center gap-5 text-[14px]">
          {timeStr && (
            <span className="inline-flex items-center gap-1.5">
              <Clock size={13} strokeWidth={2} className="text-slate-400" />
              <span className="text-slate-700 font-medium tabular-nums">{timeStr}</span>
            </span>
          )}
          {duration != null && (
            <span className="inline-flex items-center gap-1.5">
              <Timer size={13} strokeWidth={2} className="text-slate-400" />
              <span className="text-slate-600 tabular-nums">{duration} นาที</span>
            </span>
          )}
        </div>
      )}

      {apRemark && (
        <div className="rounded-lg bg-amber-50/50 border-l-2 border-amber-300 px-3 py-2 text-[14px] text-amber-900/80 italic whitespace-pre-line">
          {apRemark}
        </div>
      )}

      <MetaFooter obj={a} id={apId} />
    </Section>
  )
}

// Status pill — label + tone from the canonical table (data/appointmentStatuses.js).
// A local English map used to live here and had W/P/N wrong (W is รอติดต่อ, not Walk-in).
function StatusPill({ code }) {
  const s = apptStatusInfo(code)
  return (
    <span title={[code, s.hint].filter(Boolean).join(' · ')} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[12.5px] font-semibold ring-1 ${s.pill}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {s.label}
    </span>
  )
}

function TreatmentSection({ treatment, canEdit, onSaved }) {
  const toast = useToast()
  const initial = useMemo(() => ({
    detail: treatment?.detail || treatment?.details || '',
    remark: treatment?.remark || treatment?.note    || treatment?.internal_note || '',
  }), [treatment])

  const [mode, setMode]       = useState('view')   // 'view' | 'edit'
  const [draft, setDraft]     = useState(initial)
  const [saving, setSaving]   = useState(false)
  const [saveErr, setSaveErr] = useState(null)
  useEffect(() => { setDraft(initial) }, [initial])

  const tid     = treatment?.id ?? treatment?.treatment_id
  const dirty   = (draft.detail || '') !== (initial.detail || '') || (draft.remark || '') !== (initial.remark || '')
  const canSave = !!tid && dirty && !saving

  // Returns true once saved (false on error) — see saveAll.
  async function save() {
    if (!tid) { setSaveErr('ไม่พบ treatment id'); toast.error('บันทึกไม่สำเร็จ', { detail: 'ไม่พบ treatment id' }); return false }
    setSaving(true)
    setSaveErr(null)
    try {
      const r = await fetch(`/api/treatments/${tid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ detail: draft.detail || '', remark: draft.remark || '' }),
      })
      if (r.status === 401) { redirectToLogin(); return false }
      if (!r.ok) {
        const t = await r.text().catch(() => '')
        throw new Error(`HTTP ${r.status}: ${t || r.statusText}`)
      }
      setMode('view')
      onSaved?.()
      return true
    } catch (e) {
      const msg = e?.message || 'บันทึกไม่สำเร็จ'
      setSaveErr(msg)
      toast.error('บันทึกไม่สำเร็จ', { detail: msg })
      return false
    } finally {
      setSaving(false)
    }
  }
  function cancel() { setDraft(initial); setMode('view'); setSaveErr(null) }
  function reset()  { setDraft(initial); setSaveErr(null) }
  useChartSave('treatment', dirty, save, cancel)
  async function saveAndConfirm() {
    if (await save()) toast.success('บันทึกแล้ว')
  }

  const right = mode === 'view'
    ? (canEdit && tid && (
        <Button variant="soft" size="sm" icon={Pencil} onClick={() => { setMode('edit'); setSaveErr(null) }}>แก้ไข</Button>
      ))
    : <EditActions dirty={dirty} saving={saving} canSave={canSave} onReset={reset} onCancel={cancel} onSave={saveAndConfirm} />

  const showRemark = mode === 'edit' || !!initial.remark

  return (
    <Section icon={<Stethoscope size={14} strokeWidth={2} />} title="Treatment" subtitle="รายละเอียดการรักษา" right={right}>
      {saveErr && <ErrorState title={saveErr} />}

      <div>
        <InlineLabel icon={<FileText size={11} strokeWidth={2.25} className="text-slate-400" />} hint="ใช้ในเอกสารทางการ">
          Detail
        </InlineLabel>
        <div className="text-[15.5px] text-slate-800 leading-relaxed whitespace-pre-line">
          {mode === 'view'
            ? (initial.detail || <Muted>ยังไม่ได้บันทึก</Muted>)
            : <Textarea value={draft.detail || ''} onChange={(e) => setDraft({ ...draft, detail: e.target.value })} placeholder="รายละเอียดที่ใช้ในเอกสารทางการ" rows={3} aria-label="Detail" />
          }
        </div>
      </div>

      {showRemark && (
        <div>
          <InlineLabel icon={<StickyNote size={11} strokeWidth={2.25} className="text-slate-400" />} hint="ภายในเท่านั้น">
            Remark
          </InlineLabel>
          <div className="text-[15.5px] text-slate-700 leading-relaxed whitespace-pre-line">
            {mode === 'view'
              ? initial.remark
              : <Textarea value={draft.remark || ''} onChange={(e) => setDraft({ ...draft, remark: e.target.value })} placeholder="บันทึกภายใน (ไม่แชร์)" rows={2} aria-label="Remark" />
            }
          </div>
        </div>
      )}

      <MetaFooter obj={treatment} id={tid} />
    </Section>
  )
}

function InlineLabel({ icon, children, hint }) {
  return (
    <div className="flex items-center gap-1.5 mb-1.5">
      {icon}
      <span className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-500">{children}</span>
      {hint && <span className="text-[12.5px] text-slate-300 normal-case tracking-normal font-normal">· {hint}</span>}
    </div>
  )
}

// Edit-mode header actions shared by the Appointment and Treatment cards:
// [Reset] [ยกเลิก] [บันทึก]. Reset only appears once something differs.
function EditActions({ dirty, saving, canSave, onReset, onCancel, onSave }) {
  return (
    <div className="flex items-center gap-2">
      {dirty && (
        <Button variant="ghost" size="sm" icon={RotateCcw} onClick={onReset} title="คืนค่าตามที่บันทึกไว้ในฐานข้อมูล">
          Reset
        </Button>
      )}
      <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>ยกเลิก</Button>
      <Button size="sm" onClick={onSave} loading={saving} disabled={!canSave}>บันทึก</Button>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Atoms                                                                      */
/* -------------------------------------------------------------------------- */

function Section({ icon, title, subtitle, children, right }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl">
      <header className="px-5 pt-4 pb-3 flex items-center gap-2.5 border-b border-slate-100">
        <span className="w-7 h-7 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center ring-1 ring-slate-100">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">{title}</div>
          {subtitle && <div className="text-[13.5px] text-slate-400 -mt-0.5">{subtitle}</div>}
        </div>
        {right && <div className="flex items-center">{right}</div>}
      </header>
      <div className="p-5 space-y-5">{children}</div>
    </section>
  )
}

function Muted({ children, className = '' }) {
  return <span className={`text-slate-400 italic ${className}`}>{children}</span>
}

// Chart / Oral presence chip. Present = its colour (emerald / sky); absent = the
// neutral slate badge ("not started"), with "— ยังไม่มี" in the tooltip.
function ChipStatus({ active, icon, label, tone }) {
  const color = tone === 'sky' ? '#0ea5e9' : undefined
  return (
    <Badge
      tone={active ? (color ? 'slate' : 'emerald') : 'slate'}
      color={active ? color : undefined}
      title={active ? label : `${label} — ยังไม่มี`}
    >
      {icon}<span>{label}</span>
    </Badge>
  )
}

function MetaFooter({ obj, id }) {
  const [open, setOpen] = useState(false)
  if (!obj) return null
  const createdAt = obj._creator_at ?? obj.created_at ?? obj.created_date ?? obj.createdAt
  const updatedAt = obj._modifier_at ?? obj._modified_at ?? obj.modified_at ?? obj.updated_at ?? obj.modified_date ?? obj.updatedAt
  const createdBy = pickActorCode(obj._creator ?? obj.creator ?? obj.created_by ?? obj.createdBy)
  const updatedBy = pickActorCode(obj._modifier ?? obj.modifier ?? obj.modified_by_code ?? obj.modified_by ?? obj.updated_by)
  const hasCreated = !!(createdAt || createdBy)
  const hasUpdated = !!(updatedAt || updatedBy)
  if (id == null && !hasCreated && !hasUpdated) return null
  return (
    <div className="pt-1 flex justify-end">
      <div
        className="relative"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="meta info"
          className={`inline-flex items-center justify-center w-6 h-6 rounded-full transition-all ${
            open
              ? 'text-brand-700 bg-brand-50 ring-1 ring-brand-200'
              : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100 ring-1 ring-transparent'
          }`}
        >
          <Info size={13} strokeWidth={2} />
        </button>
        {open && (
          <div
            role="tooltip"
            className="absolute right-0 bottom-full mb-2 z-20 min-w-[168px] rounded-lg bg-white ring-1 ring-brand-200 shadow-lg shadow-brand-900/10 overflow-hidden pointer-events-none animate-ss-pop-in"
          >
            {id != null && (
              <div className="px-3 py-1.5 flex items-center justify-between gap-3 bg-brand-50">
                <span className="text-[11.5px] uppercase tracking-[0.1em] font-semibold text-brand-500">ID</span>
                <span className="font-mono text-[12.5px] text-brand-700">#{id}</span>
              </div>
            )}
            <MetaRow label="Created">
              {hasCreated
                ? <><span className="font-mono">{createdAt ? fmtDateTime(createdAt) : '—'}</span>{createdBy && <span className="text-slate-500"> · {createdBy}</span>}</>
                : <span className="italic text-slate-400">—</span>}
            </MetaRow>
            <MetaRow label="Modified" last>
              {hasUpdated
                ? <><span className="font-mono">{updatedAt ? fmtDateTime(updatedAt) : '—'}</span>{updatedBy && <span className="text-slate-500"> · {updatedBy}</span>}</>
                : <span className="italic text-slate-400">ยังไม่ถูกแก้ไข</span>}
            </MetaRow>
          </div>
        )}
      </div>
    </div>
  )
}

function MetaRow({ label, children, last }) {
  return (
    <div className={`px-3 py-1.5 ${last ? '' : 'border-b border-brand-100'}`}>
      <div className="text-[11.5px] uppercase tracking-[0.1em] font-semibold text-brand-600">{label}</div>
      <div className="text-[12.5px] text-slate-700 leading-snug">{children}</div>
    </div>
  )
}

function pickActorCode(v) {
  if (v == null || v === '') return null
  if (typeof v === 'string' || typeof v === 'number') return String(v)
  return v.code || v.employee_code || v.username || v.email || v.name || null
}

function DoctorAvatar({ src, name, size = 28 }) {
  const initial = nameInitial(name)
  return (
    <div className="rounded-full flex items-center justify-center flex-none ring-1 ring-slate-200 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50" style={{ width: size, height: size }}>
      {src
        ? <img src={src} alt={name || ''} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        : (initial !== '?' ? <span className="text-[12.5px] font-semibold text-slate-500">{initial}</span> : <User size={12} className="text-slate-400" strokeWidth={1.75} />)}
    </div>
  )
}
